import Papa from 'papaparse';

import type { CsvRow, ParsedCsv } from '../types/whatsapp';

type PapaResult = Papa.ParseResult<Record<string, string>>;

// Line 1 is the header, so the first row of data sits on line 2.
const FIRST_DATA_LINE = 2;

const isBlank = (cells: Record<string, string>) =>
  Object.values(cells).every((value) => !(value || '').trim());

const toParsedCsv = ({ data, meta, errors }: PapaResult): ParsedCsv => {
  const numbered = data.map((cells, index) => ({
    lineNumber: index + FIRST_DATA_LINE,
    cells,
    blank: isBlank(cells),
  }));
  const blankLines = new Set(
    numbered.filter((row) => row.blank).map((row) => row.lineNumber),
  );

  return {
    headerLine: (meta.fields || []).join(','),
    rows: numbered
      .filter((row) => !row.blank)
      .map(({ lineNumber, cells }): CsvRow => ({ lineNumber, cells })),
    parseErrors: errors
      .map((error) => ({
        code: error.code,
        lineNumber: (error.row ?? 0) + FIRST_DATA_LINE,
      }))
      .filter((error) => !blankLines.has(error.lineNumber)),
  };
};

export function parseBulkCsv(file: File): Promise<ParsedCsv> {
  return new Promise((resolve, reject) => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: false,
      worker: true,
      complete: (results) => resolve(toParsedCsv(results)),
      error: reject,
    });
  });
}

export async function csvFingerprint(file: File): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    await file.arrayBuffer(),
  );

  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}
